<script setup>
/* eslint-disable vue/no-unused-emit-declarations */
// Alguns emits (removeFromFunnel, viewDetails, updateValue, sendQuickMessage)
// fazem parte do contrato do board (KanbanBoard.vue escuta todos) e são
// mantidos mesmo sem uso local no template atual — ver mockup aprovado do
// hover (Onda card-mockup): card é resumo + ações rápidas.
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';
import DatePicker from 'vue-datepicker-next';
import { DATE_PICKER_LANG_PT_BR } from 'dashboard/helper/datePickerLang';
import { useStageTimer } from 'dashboard/composables/kanban/useStageTimer';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import LeadAcquisition from './LeadAcquisition.vue';
import { getDealTotalValue } from './dealValue';
import { slaHoursForStage, formatDuration, agingStatus } from './slaTime';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  stageConfig: {
    type: Object,
    required: true,
  },
  mapping: {
    type: Object,
    default: () => ({}),
  },
  funnelId: {
    type: Number,
    required: false,
    default: null,
  },
});

const emit = defineEmits([
  'click',
  'dragStart',
  'removeFromFunnel',
  'transferToFunnel',
  'winDeal',
  'loseDeal',
  'viewDetails',
  'updateValue',
  'updateAppointment',
  'sendQuickMessage',
  'updateTemperature',
]);

const store = useStore();
const { t } = useI18n();
const { currencySymbol } = useCurrency();

// Estado para edição inline do agendamento (único editor inline mantido no
// hover — ver mockup aprovado: card é resumo + ações rápidas)
const isEditingAppointment = ref(false);
const editAppointmentDate = ref(null);
const editMeetingProvider = ref('chatsuite');
const MEETING_PROVIDERS = [
  {
    value: 'chatsuite',
    labelKey: 'APPOINTMENTS.MEETING_LINK.CHATSUITE',
    icon: 'i-lucide-video',
  },
  {
    value: 'google_meet',
    labelKey: 'APPOINTMENTS.MEETING_LINK.GOOGLE_MEET',
    icon: 'i-lucide-monitor',
  },
  {
    value: 'presential',
    labelKey: 'APPOINTMENTS.MEETING_LINK.PRESENTIAL',
    icon: 'i-lucide-map-pin',
  },
];

// Timer computations
const { compactTime, isSLABreached } = useStageTimer(
  props.mapping?.stage_entered_at || props.conversation.stage_entered_at,
  props.mapping?.timer_data || {}
);

// Verificar se tem SLA violado para borda vermelha
const hasSLAViolation = computed(() => {
  return isSLABreached(props.stageConfig.sla_hours);
});

// Computed properties - Usando dados diretos da conversation para melhor performance
const currentContact = computed(() => {
  // Prioridade 1: Dados de contato direto da conversation (vem do backend do Kanban)
  // Verificar se tem dados úteis (nome ou email ou telefone)
  if (
    props.conversation.contact &&
    (props.conversation.contact.name ||
      props.conversation.contact.email ||
      props.conversation.contact.phone_number)
  ) {
    return props.conversation.contact;
  }

  // Prioridade 2: Usar dados do meta.sender diretamente (API de conversas padrão)
  if (props.conversation.meta?.sender) {
    return props.conversation.meta.sender;
  }

  // Prioridade 3: Tentar buscar da store Vuex (fallback)
  if (props.conversation.meta?.sender?.id) {
    const contact = store.getters['contacts/getContact'](
      props.conversation.meta?.sender?.id
    );
    if (contact) return contact;
  }

  return null;
});

// eslint-disable-next-line no-unused-vars
const timeInStage = computed(() => {
  if (!props.conversation.stage_entered_at) return '';

  const entered = new Date(props.conversation.stage_entered_at);
  const now = new Date();
  const diffHours = Math.floor((now - entered) / (1000 * 60 * 60));

  if (diffHours < 1) return 'Há poucos minutos';
  if (diffHours < 24) return `${diffHours}h`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d`;
});

// eslint-disable-next-line no-unused-vars
const isSlaBreached = computed(() => {
  if (!props.stageConfig.sla_hours || !props.conversation.stage_entered_at)
    return false;

  const entered = new Date(props.conversation.stage_entered_at);
  const now = new Date();
  const diffHours = (now - entered) / (1000 * 60 * 60);

  return diffHours > props.stageConfig.sla_hours;
});

// Computed para valor total - prioriza deal_value do mapping, depois Deal, depois produtos
const totalValue = computed(() =>
  getDealTotalValue(props.conversation, { funnelId: props.funnelId })
);

// Negócio ganho (inclusive carregado de outro funil via transferência): o card
// mostra o selo e o valor deixa de ler como pipeline em aberto.
const isWon = computed(() => props.mapping?.deal_status === 'won');

// Card v2: valor formatado para exibição compacta sempre visível
// (deal-rich T6). "A definir" (KANBAN.DEAL.NO_VALUE) quando ausente/zero.
const formattedTotalValue = computed(() => {
  return `${currencySymbol.value} ${totalValue.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
});

// Data mínima aceita para stage_entered_at: valores anteriores a isso (ex.:
// epoch 1970-01-01 vindo de dado legado/bugado) são tratados como "sem dado"
// para não gerar contagens absurdas de dias parado (bug do "20616d").
const MIN_VALID_STAGE_ENTERED_AT = new Date('2015-01-01T00:00:00Z').getTime();

// Computed para deal aging — mesma resolução de SLA em horas do backend
// (Task 1: Funnel#sla_hours_for_stage/#deal_age_status), via slaTime.js.
const dealAging = computed(() => {
  const stageEnteredAt =
    props.mapping?.stage_entered_at || props.conversation.stage_entered_at;
  if (!stageEnteredAt) return null;

  const enteredDate = new Date(stageEnteredAt);
  const enteredTime = enteredDate.getTime();
  if (Number.isNaN(enteredTime) || enteredTime < MIN_VALID_STAGE_ENTERED_AT) {
    return null;
  }

  const now = new Date();
  const hoursInStage = (now - enteredDate) / (1000 * 60 * 60);
  const slaHours = slaHoursForStage(props.stageConfig);
  const status = agingStatus(hoursInStage, slaHours);
  const percentage = slaHours > 0 ? (hoursInStage / slaHours) * 100 : 0;

  return {
    hoursInStage,
    slaHours,
    elapsed: formatDuration(hoursInStage),
    sla: formatDuration(slaHours),
    percentage: Math.min(100, Math.round(percentage)),
    status,
    isAging: status !== 'healthy',
  };
});

const showDealAging = computed(() => {
  // Show aging indicator if deal is at risk (warning, danger, or critical)
  return dealAging.value?.isAging || false;
});

// Cor do selo "dias parado" — escala até vermelho quando passa do prazo
const dealAgingClass = computed(() => {
  const status = dealAging.value?.status;
  if (status === 'critical') return 'bg-n-ruby-3 text-n-ruby-11';
  if (status === 'danger') return 'bg-n-amber-3 text-n-amber-12';
  return 'bg-n-amber-2 text-n-amber-11';
});

// "Rotting" (Pipedrive): parado além do limite → marca vermelha à esquerda
const isRotting = computed(() =>
  ['danger', 'critical'].includes(dealAging.value?.status)
);

// Card v2 (compacto): rótulo do estágio + tempo decorrido para o subtítulo do
// cabeçalho colapsado ("Novo Lead · há 1h").
const stageLabel = computed(() => props.stageConfig?.label || '');

const cardSubtitle = computed(() => {
  return [stageLabel.value, compactTime.value].filter(Boolean).join(' · ');
});

// A listagem padrão de conversas entrega o agente em `meta.assignee`, enquanto
// o endpoint dedicado do Kanban o entrega na raiz. Suportar os dois contratos
// mantém o dono real do atendimento visível em qualquer forma de carregamento.
const assignee = computed(
  () => props.conversation.meta?.assignee || props.conversation.assignee || null
);

const assigneeAvatar = computed(
  () => assignee.value?.thumbnail || assignee.value?.avatar_url || ''
);

const nextAction = computed(
  () =>
    props.mapping?.metadata?.next_action ||
    props.conversation.metadata?.next_action ||
    null
);

const activeNextAction = computed(() => {
  const action = nextAction.value;
  return action && !action.done && action.due_on ? action : null;
});

const nextActionTypeLabel = computed(() => {
  const labels = {
    call: t('KANBAN.DEAL.NEXT_ACTION_TYPES.CALL'),
    message: t('KANBAN.DEAL.NEXT_ACTION_TYPES.MESSAGE'),
    meeting: t('KANBAN.DEAL.NEXT_ACTION_TYPES.MEETING'),
    other: t('KANBAN.DEAL.NEXT_ACTION_TYPES.OTHER'),
  };
  return labels[activeNextAction.value?.type] || labels.other;
});

const nextActionDate = computed(() => {
  const value = activeNextAction.value?.due_on;
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
});

const formatarQuando = iso => {
  const date = new Date(iso);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}/${month} ${hours}:${minutes}`;
};

// Início do agendamento: scheduled_at (coluna real) com fallback no legado
// appointment_date.
const appointmentStart = apt => apt.scheduled_at || apt.appointment_date;

// Agendamentos ativos que ainda não começaram (início >= agora), do mais
// próximo para o mais distante.
const upcomingAppointments = computed(() => {
  const now = Date.now();
  return (props.conversation.conversation_appointments || [])
    .filter(apt => apt.status === 'scheduled' || apt.status === 'confirmed')
    .map(apt => ({ apt, start: new Date(appointmentStart(apt)).getTime() }))
    .filter(({ start }) => !Number.isNaN(start) && start >= now)
    .sort((a, b) => a.start - b.start)
    .map(({ apt }) => apt);
});

// Data/hora do próximo agendamento mostrada no chip do card
const appointmentDateTime = computed(() => {
  // O agendamento do cliente costuma estar em OUTRA conversa dele: o funil
  // guarda um cartão por contato e religa o cartão à conversa nova, enquanto a
  // reunião fica na antiga. O servidor manda o próximo compromisso do contato
  // justamente por isso — sem ele o cartão dizia "Agendar" para quem já tinha
  // horário marcado.
  const doContato = props.conversation.next_appointment;
  if (doContato?.scheduled_at) return formatarQuando(doContato.scheduled_at);

  // Sem o campo novo (payload antigo em cache), vale o que a conversa tem —
  // mas só o que ainda vai acontecer: um "scheduled" que já passou não é o
  // próximo compromisso (o card mostrava a reunião de ontem como próxima).
  const upcoming = upcomingAppointments.value[0];
  return upcoming ? formatarQuando(appointmentStart(upcoming)) : null;
});

// Próxima atividade (assinatura Pipedrive): verde quando agendada, âmbar quando falta
const nextActivity = computed(() => {
  if (appointmentDateTime.value) {
    return {
      label: appointmentDateTime.value,
      dotClass: 'bg-n-teal-9',
      textClass: 'text-n-slate-11',
    };
  }
  return {
    label: t('KANBAN.DEAL.SCHEDULE'),
    dotClass: 'bg-n-amber-9',
    textClass: 'text-n-amber-11',
  };
});

// Temperatura, score de IA e checklist saíram do rosto do card (declutter —
// padrão Pipedrive/Attio). Continuam disponíveis na página do negócio.

// Última mensagem + quem falou por último (sinaliza "bola em quadra de quem")
const messagePreview = computed(() => {
  const content = props.conversation.last_non_activity_message?.content || '';
  return content.length > 80 ? content.substring(0, 80) + '…' : content;
});

const lastMessageSender = computed(() => {
  const lastMessage = props.conversation.last_non_activity_message;
  if (!lastMessage) return '';
  // message_type: 0 = incoming (cliente), 1 = outgoing (você/agente)
  return lastMessage.message_type === 0 ? 'cliente' : 'agente';
});

// Methods - Removidos, usando dados nativos direto no template

// Event handlers
const handleClick = () => {
  emit('click');
};

const handleTransferToFunnel = event => {
  event.stopPropagation();
  emit('transferToFunnel', props.conversation);
};

// Excluir: remove o negócio do funil (KanbanView.handleCardRemove recebe a conversa)
const handleRemoveFromFunnel = event => {
  event.stopPropagation();
  emit('removeFromFunnel', props.conversation);
};

const handleDragStart = event => {
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData(
    'conversationId',
    props.conversation.id.toString()
  );
  event.dataTransfer.setData(
    'conversationData',
    JSON.stringify(props.conversation)
  );

  // Adicionar classe de dragging
  event.target.classList.add('dragging');

  emit('dragStart');
};

const handleDragEnd = event => {
  // Remover classe de dragging
  event.target.classList.remove('dragging');
};

// Win/Lose deal handlers
const handleWinDeal = event => {
  event.stopPropagation();
  emit('winDeal', {
    conversation: props.conversation,
    mapping: props.mapping,
    currentValue: totalValue.value,
  });
};

const handleLoseDeal = event => {
  event.stopPropagation();
  emit('loseDeal', {
    conversation: props.conversation,
    mapping: props.mapping,
  });
};

// Inline Appointment Editing
// Data/hora do próximo agendamento em formato aceito pelo <input datetime-local>
// (YYYY-MM-DDTHH:mm, hora local) — usado para pré-preencher ao reagendar.
const nextAppointment = computed(() => upcomingAppointments.value[0]);

const startEditAppointment = event => {
  event.stopPropagation();
  // Reagendar já vem com a data atual preenchida; agendar novo começa vazio.
  editAppointmentDate.value = nextAppointment.value
    ? new Date(appointmentStart(nextAppointment.value))
    : null;
  editMeetingProvider.value =
    nextAppointment.value?.meeting_provider ||
    store.getters.getCurrentAccount?.settings?.default_meeting_provider ||
    'chatsuite';
  isEditingAppointment.value = true;
};

// Confirma o agendamento (botão explícito) — evita o disparo torto do @change
// do datetime-local e deixa claro pro usuário quando de fato agenda.
const confirmAppointment = () => {
  if (!editAppointmentDate.value) return;
  emit('updateAppointment', {
    conversation: props.conversation,
    mapping: props.mapping,
    date: editAppointmentDate.value,
    meetingProvider: editMeetingProvider.value,
    // Reagendamento reusa o agendamento existente (evita duplicata).
    appointmentId: nextAppointment.value?.id || null,
  });
  isEditingAppointment.value = false;
};

const cancelEditAppointment = event => {
  event?.stopPropagation();
  isEditingAppointment.value = false;
};

// Abrir conversa diretamente
const openConversation = event => {
  event.stopPropagation();
  const accountId = props.conversation.account_id;
  const conversationId = props.conversation.id;
  window.open(
    `/app/accounts/${accountId}/conversations/${conversationId}`,
    '_blank'
  );
};
</script>

<template>
  <div
    class="kanban-card group/card relative rounded-xl shadow-sm p-3 cursor-pointer backdrop-blur-sm text-n-slate-12 border border-n-weak"
    :class="{ 'sla-violation': hasSLAViolation }"
    draggable="true"
    @click="handleClick"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
  >
    <!-- Rotting (Pipedrive): barra vermelha à esquerda quando o negócio ficou parado além do limite -->
    <span
      v-if="isRotting"
      class="pointer-events-none absolute inset-y-2 left-0 w-1 rounded-full bg-n-ruby-9"
      data-testid="kanban-card-rotting"
    />

    <!-- 1. Cabeçalho: avatar + nome + subtítulo (estágio · tempo) + chip de score IA -->
    <div class="flex items-center gap-2.5">
      <div class="flex-shrink-0">
        <img
          v-if="
            currentContact?.avatar_url ||
            currentContact?.avatar ||
            currentContact?.thumbnail
          "
          :src="
            currentContact.avatar_url ||
            currentContact.avatar ||
            currentContact.thumbnail
          "
          :alt="currentContact?.name"
          loading="lazy"
          class="h-9 w-9 rounded-full object-cover"
          @error="
            e => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }
          "
        />
        <div
          :style="{
            display:
              currentContact?.avatar_url ||
              currentContact?.avatar ||
              currentContact?.thumbnail
                ? 'none'
                : 'flex',
          }"
          class="h-9 w-9 items-center justify-center rounded-full bg-n-slate-4 text-sm font-semibold text-n-slate-11"
        >
          {{
            (currentContact?.name || 'U')
              .split(' ')
              .map(n => n[0])
              .join('')
              .substring(0, 2)
              .toUpperCase()
          }}
        </div>
      </div>

      <div class="min-w-0 flex-1">
        <div class="flex items-baseline gap-2">
          <p
            class="min-w-0 flex-1 truncate text-sm font-medium text-n-slate-12"
          >
            {{ currentContact?.name || 'Usuário' }}
          </p>
          <span
            v-if="isWon"
            class="inline-flex shrink-0 items-center gap-1 rounded-full bg-n-teal-3 px-1.5 py-0.5 text-[10px] font-semibold text-n-teal-11"
            data-testid="kanban-card-won-badge"
          >
            <span class="i-lucide-check text-[10px]" />
            {{ $t('KANBAN.DEAL.WON_BADGE') }}
          </span>
          <span
            v-if="totalValue > 0"
            class="shrink-0 text-sm font-medium tabular-nums"
            :class="isWon ? 'text-n-teal-11' : 'text-n-slate-12'"
            data-testid="kanban-card-value"
          >
            {{ formattedTotalValue }}
          </span>
        </div>
        <p class="truncate text-[11px] text-n-slate-10">
          {{ cardSubtitle }}
        </p>
      </div>
    </div>

    <!-- Próxima atividade (assinatura Pipedrive) + dias parado (rotting) -->
    <div class="mt-2.5 flex items-center gap-2 text-[11px]">
      <div
        v-if="activeNextAction"
        v-tooltip.top="activeNextAction.note || nextActionTypeLabel"
        class="flex min-w-0 flex-1 items-center gap-1.5 rounded-md bg-n-alpha-1 px-2 py-1"
        data-testid="kanban-card-next-action"
      >
        <span class="i-lucide-list-checks shrink-0 text-sm text-n-blue-11" />
        <span class="min-w-0 flex-1 truncate font-medium text-n-slate-11">
          {{ nextActionTypeLabel
          }}<template v-if="activeNextAction.note">
            — {{ activeNextAction.note }}</template
          >
        </span>
        <span class="shrink-0 font-semibold text-n-slate-10">
          {{ nextActionDate }}
        </span>
      </div>
      <!-- Chip clicável: abre o seletor de data/hora (agendar ou reagendar) -->
      <button
        v-else
        v-tooltip.top="$t('KANBAN.DEAL.EDIT_APPOINTMENT')"
        type="button"
        class="inline-flex min-w-0 items-center gap-1.5 rounded-md px-1 py-0.5 -mx-1 transition-colors hover:bg-n-alpha-1"
        data-testid="kanban-card-next-activity"
        @click.stop="startEditAppointment"
      >
        <span
          class="h-2 w-2 shrink-0 rounded-full"
          :class="nextActivity.dotClass"
        />
        <span class="truncate" :class="nextActivity.textClass">
          {{ nextActivity.label }}
        </span>
      </button>
      <div
        v-tooltip.top="
          `${t('KANBAN.DEAL.RESPONSIBLE')}: ${assignee?.name || t('KANBAN.DEAL.UNASSIGNED')}`
        "
        class="ml-auto flex min-w-0 max-w-[48%] items-center gap-1.5 rounded-full bg-n-alpha-1 py-0.5 pl-0.5 pr-2"
        data-testid="kanban-card-assignee"
      >
        <Avatar
          v-if="assignee"
          :src="assigneeAvatar"
          :name="assignee.name || ''"
          :size="20"
          rounded-full
          class="shrink-0"
        />
        <span
          v-else
          class="i-lucide-user-round-x ml-1 shrink-0 text-sm text-n-slate-9"
        />
        <span class="truncate text-[10px] font-medium text-n-slate-11">
          {{ assignee?.name || t('KANBAN.DEAL.UNASSIGNED') }}
        </span>
      </div>
      <span
        v-if="showDealAging && dealAging"
        class="inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
        :class="dealAgingClass"
      >
        <span class="i-lucide-clock text-[10px]" />
        {{
          t('KANBAN.SLA_BADGE', {
            elapsed: dealAging.elapsed,
            sla: dealAging.sla,
          })
        }}
      </span>
    </div>

    <LeadAcquisition :acquisition="conversation.acquisition" />

    <!-- Última mensagem: quem falou por último + prévia (bola em quadra de quem?) -->
    <div
      v-if="messagePreview"
      class="mt-2 flex min-w-0 items-center gap-1.5"
      data-testid="kanban-card-last-message"
    >
      <span
        class="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold"
        :class="
          lastMessageSender === 'cliente' ? 'text-n-teal-11' : 'text-n-slate-10'
        "
      >
        <span
          :class="
            lastMessageSender === 'cliente'
              ? 'i-lucide-arrow-down-left'
              : 'i-lucide-arrow-up-right'
          "
          class="text-xs"
        />
        {{ lastMessageSender === 'cliente' ? 'Cliente' : 'Você' }}
      </span>
      <span class="truncate text-[11px] text-n-slate-10">
        {{ messagePreview }}
      </span>
    </div>

    <!-- Ações: reveladas no hover — card limpo por padrão (padrão Pipedrive/Attio) -->
    <div
      class="grid grid-rows-[0fr] opacity-0 transition-all duration-200 ease-out group-hover/card:grid-rows-[1fr] group-hover/card:opacity-100"
      data-testid="kanban-card-quick-actions"
    >
      <div class="min-h-0 overflow-hidden">
        <div
          class="mt-2.5 flex items-center gap-1.5 border-t border-n-weak pt-2.5"
        >
          <button
            v-tooltip.top="$t('KANBAN.QUICK_ACTIONS.OPEN_CONVERSATION')"
            type="button"
            class="flex h-8 w-8 items-center justify-center rounded-lg bg-n-alpha-1 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            data-testid="kanban-card-open"
            @click.stop="openConversation"
          >
            <span class="i-lucide-external-link text-lg" />
          </button>
          <button
            v-tooltip.top="$t('KANBAN.DEAL.SCHEDULE')"
            type="button"
            class="flex h-8 w-8 items-center justify-center rounded-lg bg-n-alpha-1 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            data-testid="kanban-card-schedule"
            @click.stop="startEditAppointment"
          >
            <span class="i-lucide-calendar text-lg" />
          </button>
          <button
            v-tooltip.top="$t('KANBAN.QUICK_ACTIONS.TRANSFER_TO_FUNNEL')"
            type="button"
            class="flex h-8 w-8 items-center justify-center rounded-lg bg-n-alpha-1 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            data-testid="kanban-card-move-funnel"
            @click.stop="handleTransferToFunnel"
          >
            <span class="i-lucide-arrow-left-right text-lg" />
          </button>

          <div class="ml-auto flex items-center gap-1.5">
            <button
              v-tooltip.top="$t('KANBAN.DEAL.WIN.BUTTON_TOOLTIP')"
              type="button"
              class="flex h-8 w-8 items-center justify-center rounded-lg bg-n-teal-3 text-n-teal-11 transition-colors hover:bg-n-teal-4"
              data-testid="kanban-card-win"
              @click.stop="handleWinDeal"
            >
              <span class="i-lucide-trophy text-lg" />
            </button>
            <button
              v-tooltip.top="$t('KANBAN.DEAL.LOSE.BUTTON_TOOLTIP')"
              type="button"
              class="flex h-8 w-8 items-center justify-center rounded-lg bg-n-ruby-3 text-n-ruby-11 transition-colors hover:bg-n-ruby-4"
              data-testid="kanban-card-lose"
              @click.stop="handleLoseDeal"
            >
              <span class="i-lucide-circle-x text-lg" />
            </button>
            <button
              v-tooltip.top="$t('KANBAN.QUICK_ACTIONS.REMOVE_FROM_FUNNEL')"
              type="button"
              class="flex h-8 w-8 items-center justify-center rounded-lg bg-n-alpha-1 text-n-slate-11 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11"
              data-testid="kanban-card-remove"
              @click.stop="handleRemoveFromFunnel"
            >
              <span class="i-lucide-trash-2 text-lg" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Editor inline de agendamento (fora do hover para não sumir ao editar).
         Input de data+hora em linha própria (largura total) pra caber dia/mês/ano
         E o horário; botões abaixo. -->
    <div
      v-if="isEditingAppointment"
      class="mt-2 flex flex-col gap-2 rounded-lg border border-n-weak bg-n-solid-2 p-2.5"
      data-testid="kanban-card-appointment-editor"
      @click.stop
    >
      <span
        class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
      >
        {{ $t('KANBAN.DEAL.SCHEDULE') }}
      </span>
      <div class="date-picker small no-margin auto-width">
        <DatePicker
          v-model:value="editAppointmentDate"
          type="datetime"
          format="DD/MM/YYYY HH:mm"
          :minute-step="5"
          :clearable="false"
          :editable="false"
          confirm
          confirm-text="OK"
          :lang="DATE_PICKER_LANG_PT_BR"
          :placeholder="$t('KANBAN.DEAL.SCHEDULE')"
        />
      </div>
      <div class="flex items-center gap-1">
        <button
          v-for="provider in MEETING_PROVIDERS"
          :key="provider.value"
          v-tooltip.top="$t(provider.labelKey)"
          type="button"
          class="flex h-7 flex-1 items-center justify-center gap-1 rounded-md border text-[11px] font-medium transition-colors"
          :class="
            editMeetingProvider === provider.value
              ? 'border-n-brand bg-n-blue-3 text-n-blue-11'
              : 'border-n-weak text-n-slate-10 hover:text-n-slate-12'
          "
          :data-testid="`kanban-card-meeting-${provider.value}`"
          @click="editMeetingProvider = provider.value"
        >
          <span :class="provider.icon" class="text-sm" />
        </button>
      </div>
      <div class="flex items-center gap-1.5">
        <button
          type="button"
          class="flex h-7 flex-1 items-center justify-center gap-1 rounded-md bg-n-brand px-2 text-[11px] font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-40"
          data-testid="kanban-card-appointment-confirm"
          :disabled="!editAppointmentDate"
          @click="confirmAppointment"
        >
          <span class="i-lucide-calendar-check text-sm" />
          {{ $t('KANBAN.DEAL.SCHEDULE') }}
        </button>
        <button
          v-tooltip.top="$t('KANBAN.DEAL.CANCEL')"
          type="button"
          class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-n-alpha-1 text-n-slate-11 transition-colors hover:bg-n-alpha-2"
          data-testid="kanban-card-appointment-cancel"
          @click="cancelEditAppointment"
        >
          <span class="i-lucide-x text-sm" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.kanban-card {
  position: relative;
}

.kanban-card.dragging {
  opacity: 0.7;
  cursor: grabbing !important;
  transform: rotate(2deg);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
}
</style>

<style>
/* KanbanCard - superfície translúcida (suavidade / glass) */
.kanban-card {
  background-color: rgba(255, 255, 255, 0.85) !important;
  border-color: rgb(var(--border-weak) / 0.9) !important;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  transition:
    transform 0.16s ease,
    box-shadow 0.16s ease,
    background-color 0.16s ease,
    border-color 0.16s ease;
}

/* KanbanCard - Tema escuro */
.dark .kanban-card {
  background-color: rgba(22, 30, 50, 0.82) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* Hover: o card "ganha vida" — sobe, borda accent e fundo mais sólido */
.kanban-card:hover {
  background-color: #ffffff !important;
  border-color: rgb(var(--blue-9)) !important;
  transform: translateY(-2px);
  box-shadow: 0 10px 24px -10px rgba(10, 37, 64, 0.22);
}

.dark .kanban-card:hover {
  background-color: rgba(30, 40, 64, 0.95) !important;
  border-color: rgb(var(--blue-9)) !important;
  box-shadow: 0 12px 28px -12px rgba(0, 0, 0, 0.6);
}

/* Respeita preferência de menos movimento */
@media (prefers-reduced-motion: reduce) {
  .kanban-card {
    transition: none;
  }
  .kanban-card:hover {
    transform: none;
  }
}

/* SLA Violation - tema claro */
.kanban-card.sla-violation {
  background-color: #fef2f2 !important;
  border-color: #fca5a5 !important; /* red-300 - mais suave */
}

/* SLA Violation - tema escuro - mais suave */
.dark .kanban-card.sla-violation {
  background-color: rgba(127, 29, 29, 0.25) !important; /* mais transparente */
  border-color: rgba(
    248,
    113,
    113,
    0.4
  ) !important; /* red-400 com 40% opacidade */
}

/* Elementos internos do card - Dark mode */
.dark .kanban-card .bg-slate-100,
.dark .kanban-card .bg-slate-50 {
  background-color: #334155 !important;
}

.dark .kanban-card .text-slate-600,
.dark .kanban-card .text-slate-500 {
  color: #94a3b8 !important;
}

.dark .kanban-card .text-slate-800,
.dark .kanban-card .text-slate-900 {
  color: #f1f5f9 !important;
}

.dark .kanban-card .bg-slate-200 {
  background-color: #475569 !important;
}

.dark .kanban-card.dragging {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5);
}

/* Kanban icon colors - forced with !important to override card-level color inheritance */
.kanban-icon-purple {
  color: #a855f7 !important;
}
</style>
